import {
  CoreLanguageType,
  LanguageCode,
  LanguageType,
  LocaleDetails,
  proxyLanguageRecord,
} from 'shared/src/types/languages.type';

type DemoType = {
  comparison: {
    presentation: string;
    try: string;
  };
  category: {
    presentation: string;
    try: string;
  };
  article: {
    presentation: string;
    try: string;
  };
  /**
   * Concrete example queries fed to the rotating search-bar placeholder and
   * the tap-to-fill suggestion chips. Optional — languages without a curated
   * list derive a pair from category.try / comparison.try (never mixed with
   * English extras). Keep them short, first-person, and typeable; they are
   * served lowercase-initial (see getSearchExamples).
   */
  examples?: string[];
};

const demoContentCore: Record<CoreLanguageType, DemoType> = {
  [LanguageCode.english]: {
    comparison: {
      presentation: 'Search a product to see its top competitors.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Describe a need or problem. We'll find the right products.`,
      try: `I want a hiking backpack for women`,
    },
    article: {
      presentation: `Search for an article to create it.`,
      try: `I want an article on the importance of free content to attract customers, addressing the four main customer acquisition strategies (Warm Outreach, Free Content Publishing, Paid Advertising, Cold Outreach) and focusing on how targeted free content creation can establish your expertise, build trust with prospects, and convert visitors into loyal customers, while improving your online visibility and credibility.`,
    },
    examples: [
      // Phrased without the leading "I want" so the lowercase-initial rule
      // below doesn't produce a bare "i", which reads as a typo in English.
      `a hiking backpack for women`,
      `iPhone 18 Pro`,
      `a quiet coffee grinder for a small kitchen`,
      `a gift for my dad who loves grilling`,
    ],
  },
  [LanguageCode.french]: {
    comparison: {
      presentation: 'Recherchez un produit pour voir ses meilleurs concurrents.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: 'Décrivez un besoin ou un problème. On trouve les bons produits.',
      try: `iPad pour prendre des note avec un stylet pour étudiante en enseignement`,
    },
    article: {
      presentation: 'Recherchez un article pour le créer. ',
      try: `Je veux un article sur l'importance du contenu gratuit pour attirer des clients, en abordant les quatre principales stratégies d'acquisition de clients (Warm Outreach, Publication de contenu gratuit, Publicité payante, Cold Outreach) et en mettant l'accent sur la manière dont la création de contenu gratuit ciblé peut établir votre expertise, gagner la confiance des prospects, et transformer les visiteurs en clients fidèles, tout en améliorant votre visibilité et votre crédibilité en ligne.`,
    },
    examples: [
      `Je veux un sac à dos de randonnée pour femme`,
      `iPhone 18 Pro`,
      `un moulin à café silencieux pour une petite cuisine`,
      `un cadeau pour mon père qui adore le barbecue`,
    ],
  },
  [LanguageCode.spanish]: {
    comparison: {
      presentation: 'Busca un producto para ver sus principales competidores.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Describe una necesidad o problema — encontramos los productos adecuados.`,
      try: `Quiero una mochila de senderismo para mujeres`,
    },
    article: {
      presentation: `Busca un artículo para crearlo.`,
      try: `Quiero un artículo sobre la importancia del contenido gratuito para atraer clientes, abordando las cuatro principales estrategias de adquisición de clientes (Warm Outreach, Publicación de contenido gratuito, Publicidad pagada, Cold Outreach) y centrándose en cómo la creación de contenido gratuito dirigido puede establecer tu experiencia, generar confianza con los prospectos y convertir visitantes en clientes leales, al mismo tiempo que mejora tu visibilidad y credibilidad en línea.`,
    },
  },
  [LanguageCode.german]: {
    comparison: {
      presentation: 'Suchen Sie nach einem Produkt, um seine besten Konkurrenten zu sehen.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Beschreiben Sie ein Bedürfnis oder Problem — wir finden die passenden Produkte.`,
      try: `Ich möchte einen Wanderrucksack für Frauen`,
    },
    article: {
      presentation: `Suchen Sie nach einem Artikel, um ihn zu erstellen.`,
      try: `Ich möchte einen Artikel über die Bedeutung kostenloser Inhalte zur Kundengewinnung, der die vier wichtigsten Strategien zur Kundenakquise (Warm Outreach, Veröffentlichung kostenloser Inhalte, bezahlte Werbung, Cold Outreach) behandelt und sich darauf konzentriert, wie die Erstellung gezielter kostenloser Inhalte Ihre Expertise etablieren, Vertrauen bei Interessenten aufbauen und Besucher in treue Kunden verwandeln kann, während Sie Ihre Online-Sichtbarkeit und Glaubwürdigkeit verbessern.`,
    },
  },
  [LanguageCode.italian]: {
    comparison: {
      presentation: 'Cerca un prodotto per vedere i suoi principali concorrenti.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Cerca una categoria per trovare i migliori prodotti.`,
      try: `Voglio uno zaino da trekking da donna`,
    },
    article: {
      presentation: `Cerca un articolo per crearlo.`,
      try: `Voglio un articolo sull'importanza dei contenuti gratuiti per attirare clienti, che affronti le quattro principali strategie di acquisizione (Warm Outreach, pubblicazione di contenuti gratuiti, pubblicità a pagamento, Cold Outreach) e si concentri su come la creazione mirata di contenuti gratuiti possa consolidare la vostra competenza, creare fiducia con i potenziali clienti e trasformare i visitatori in clienti fedeli, migliorando visibilità e credibilità online.`,
    },
  },
  [LanguageCode.portugueseBR]: {
    comparison: {
      presentation: 'Busque um produto para ver seus principais concorrentes.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Busque uma categoria para encontrar os melhores produtos.`,
      try: `Quero uma mochila de trilha para mulheres`,
    },
    article: {
      presentation: `Busque um artigo para criá-lo.`,
      try: `Quero um artigo sobre a importância do conteúdo gratuito para atrair clientes, abordando as quatro principais estratégias de aquisição (Warm Outreach, publicação de conteúdo gratuito, publicidade paga, Cold Outreach) e focando em como a criação direcionada de conteúdo gratuito pode estabelecer sua expertise, gerar confiança com prospects e converter visitantes em clientes fiéis, melhorando visibilidade e credibilidade online.`,
    },
  },
  [LanguageCode.portuguesePT]: {
    comparison: {
      presentation: 'Pesquise um produto para ver os seus principais concorrentes.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Pesquise uma categoria para encontrar os melhores produtos.`,
      try: `Quero uma mochila de caminhada para mulheres`,
    },
    article: {
      presentation: `Pesquise um artigo para o criar.`,
      try: `Quero um artigo sobre a importância do conteúdo gratuito para atrair clientes, abordando as quatro principais estratégias de aquisição (Warm Outreach, publicação de conteúdo gratuito, publicidade paga, Cold Outreach) e focando na forma como a criação de conteúdo gratuito direcionado pode estabelecer a sua experiência, criar confiança com potenciais clientes e converter visitantes em clientes fiéis, melhorando a visibilidade e credibilidade online.`,
    },
  },
  [LanguageCode.polish]: {
    comparison: {
      presentation: 'Wyszukaj produkt, aby zobaczyć jego najlepszych konkurentów.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Wyszukaj kategorię, aby znaleźć najlepsze produkty.`,
      try: `Chcę plecak turystyczny dla kobiet`,
    },
    article: {
      presentation: `Wyszukaj artykuł, aby go utworzyć.`,
      try: `Chcę artykuł o znaczeniu darmowych treści dla przyciągania klientów, omawiający cztery główne strategie pozyskiwania klientów (Warm Outreach, publikacja darmowych treści, płatna reklama, Cold Outreach) i skupiający się na tym, jak tworzenie ukierunkowanych darmowych treści może ugruntować Państwa ekspertyzę, budować zaufanie wśród potencjalnych klientów i przekształcać odwiedzających w lojalnych klientów, jednocześnie poprawiając widoczność i wiarygodność w internecie.`,
    },
  },
  [LanguageCode.dutch]: {
    comparison: {
      presentation: 'Zoek een product om de beste concurrenten te bekijken.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Zoek een categorie om de beste producten te vinden.`,
      try: `Ik wil een wandelrugzak voor vrouwen`,
    },
    article: {
      presentation: `Zoek een artikel om het aan te maken.`,
      try: `Ik wil een artikel over het belang van gratis content om klanten aan te trekken, waarin de vier belangrijkste strategieën voor klantenwerving worden behandeld (Warm Outreach, publicatie van gratis content, betaalde reclame, Cold Outreach), met de nadruk op hoe gerichte gratis content uw expertise kan vestigen, vertrouwen bij potentiële klanten kan opbouwen en bezoekers kan omzetten in loyale klanten, terwijl uw online zichtbaarheid en geloofwaardigheid verbeteren.`,
    },
  },
  [LanguageCode.galician]: {
    comparison: {
      presentation: 'Procure un produto para ver os seus mellores competidores.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Procure unha categoría para atopar os mellores produtos.`,
      try: `Quero unha mochila de sendeirismo para mulleres`,
    },
    article: {
      presentation: `Procure un artigo para crealo.`,
      try: `Quero un artigo sobre a importancia do contido gratuíto para atraer clientes, abordando as catro principais estratexias de adquisición de clientes (Warm Outreach, publicación de contido gratuíto, publicidade de pago, Cold Outreach) e centrándose en como a creación de contido gratuíto dirixido pode establecer a súa experiencia, xerar confianza cos potenciais clientes e converter visitantes en clientes fieis, ao tempo que mellora a súa visibilidade e credibilidade en liña.`,
    },
  },
  [LanguageCode.scottishGaelic]: {
    comparison: {
      presentation: 'Lorg bathar gus na prìomh fharpaisich aige fhaicinn.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Lorg roinn-seòrsa gus na batharan as fheàrr a lorg.`,
      try: `Tha mi ag iarraidh poca-droma coiseachd airson boireannaich`,
    },
    article: {
      presentation: `Lorg airson artaigil gus a chruthachadh.`,
      try: `Tha mi ag iarraidh artaigil mu cho cudromach 's a tha susbaint an-asgaidh gus luchd-ceannach a thàladh, a' dèiligeadh ris na ceithir prìomh ro-innleachdan airson luchd-ceannach fhaighinn (Ruigsinn Blàth, Foillseachadh Susbaint An-asgaidh, Sanasachd Phàighte, Ruigsinn Fhuar) agus a' cuimseachadh air mar a dh'fhaodas cruthachadh susbaint an-asgaidh cuimsichte do chuid eòlais a stèidheachadh, earbsa a thogail le daoine, agus luchd-tadhail a thionndadh gu luchd-ceannach dìleas, fhad 's a bhios tu a' leasachadh do fhaicsinneachd agus do chreideas air-loidhne.`,
    },
  },
  [LanguageCode.swedish]: {
    comparison: {
      presentation: 'Sök efter en produkt för att se dess främsta konkurrenter.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Sök en kategori för att hitta de bästa produkterna.`,
      try: `Jag vill ha en vandringsryggsäck för kvinnor`,
    },
    article: {
      presentation: `Sök efter en artikel för att skapa den.`,
      try: `Jag vill ha en artikel om vikten av gratis innehåll för att attrahera kunder, med de fyra huvudstrategierna för kundanskaffning (Warm Outreach, publicering av gratis innehåll, betald annonsering, Cold Outreach) och fokus på hur skräddarsytt gratis innehåll kan bygga din expertis, skapa förtroende hos potentiella kunder och omvandla besökare till lojala kunder, samtidigt som det förbättrar din synlighet och trovärdighet online.`,
    },
  },
  [LanguageCode.telugu]: {
    comparison: {
      presentation: 'ఒక ఉత్పత్తిని శోధించి దాని ప్రముఖ పోటీదారులను చూడండి.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `ఒక వర్గాన్ని శోధించి ఉత్తమ ఉత్పత్తులను కనుగొనండి.`,
      try: `మహిళలకు హైకింగ్ బ్యాక్‌ప్యాక్ కావాలి`,
    },
    article: {
      presentation: `ఒక ఆర్టికల్ కోసం శోధించి దానిని సృష్టించండి.`,
      try: `కస్టమర్లను ఆకర్షించడానికి ఉచిత కంటెంట్ ప్రాముఖ్యతపై ఒక ఆర్టికల్ కావాలి, నాలుగు ప్రధాన కస్టమర్ సంపాదన వ్యూహాలను (Warm Outreach, ఉచిత కంటెంట్ ప్రచురణ, చెల్లింపు ప్రకటనలు, Cold Outreach) చర్చిస్తూ, లక్ష్యిత ఉచిత కంటెంట్ సృష్టి మీ నైపుణ్యాన్ని ఎలా స్థాపిస్తుందో, సంభావ్య కస్టమర్లతో విశ్వాసాన్ని ఎలా పెంచుతుందో, మరియు సందర్శకులను విశ్వసనీయ కస్టమర్లుగా ఎలా మారుస్తుందో, అదే సమయంలో మీ ఆన్‌లైన్ దృశ్యమానత మరియు విశ్వసనీయతను ఎలా మెరుగుపరుస్తుందో వివరిస్తుంది.`,
    },
  },
  [LanguageCode.bengali]: {
    comparison: {
      presentation: 'শীর্ষ প্রতিযোগীদের দেখতে একটি পণ্য অনুসন্ধান করুন।',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `সেরা পণ্যগুলি খুঁজে পেতে একটি বিভাগ অনুসন্ধান করুন।`,
      try: `আমি মহিলাদের জন্য একটি হাইকিং ব্যাকপ্যাক চাই`,
    },
    article: {
      presentation: `একটি নিবন্ধ তৈরি করতে অনুসন্ধান করুন।`,
      try: `আমি গ্রাহক আকৃষ্ট করতে বিনামূল্যে কন্টেন্টের গুরুত্ব নিয়ে একটি নিবন্ধ চাই, যেখানে গ্রাহক অর্জনের চারটি প্রধান কৌশল (Warm Outreach, Free Content Publishing, Paid Advertising, Cold Outreach) আলোচনা করা হবে এবং কীভাবে লক্ষ্যভিত্তিক বিনামূল্যে কন্টেন্ট তৈরি আপনার দক্ষতা প্রতিষ্ঠিত করতে পারে, সম্ভাব্য গ্রাহকদের সাথে আস্থা তৈরি করতে পারে এবং দর্শকদের বিশ্বস্ত গ্রাহকে পরিণত করতে পারে, পাশাপাশি আপনার অনলাইন দৃশ্যমানতা ও বিশ্বাসযোগ্যতা উন্নত করতে পারে তার উপর দৃষ্টি নিবদ্ধ করা হবে।`,
    },
  },
  [LanguageCode.welsh]: {
    comparison: {
      presentation: 'Chwiliwch am gynnyrch i weld ei brif gystadleuwyr.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Chwiliwch am gategori i ddod o hyd i'r cynhyrchion gorau.`,
      try: `Rwyf eisiau sach deithio ar gyfer menywod`,
    },
    article: {
      presentation: `Chwiliwch am erthygl i'w chreu.`,
      try: `Rwyf eisiau erthygl am bwysigrwydd cynnwys am ddim i ddenu cwsmeriaid, gan drafod y pedair prif strategaeth caffael cwsmeriaid (Allgymorth Cynnes, Cyhoeddi Cynnwys Am Ddim, Hysbysebu Taledig, Allgymorth Oer) a chanolbwyntio ar sut y gall creu cynnwys am ddim wedi'i dargedu sefydlu eich arbenigedd, adeiladu ymddiriedaeth gyda darpar gwsmeriaid, a throi ymwelwyr yn gwsmeriaid ffyddlon, tra'n gwella eich gwelededd a'ch hygrededd ar-lein.`,
    },
  },
  [LanguageCode.marathi]: {
    comparison: {
      presentation: 'उत्पादनाचे प्रमुख स्पर्धक पाहण्यासाठी शोधा.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `सर्वोत्तम उत्पादने शोधण्यासाठी श्रेणी शोधा.`,
      try: `मला महिलांसाठी हायकिंग बॅकपॅक हवी आहे`,
    },
    article: {
      presentation: `लेख तयार करण्यासाठी शोधा.`,
      try: `मला ग्राहकांना आकर्षित करण्यासाठी मोफत सामग्रीच्या महत्त्वावर एक लेख हवा आहे, ज्यामध्ये ग्राहक संपादनाच्या चार प्रमुख रणनीतींचा (Warm Outreach, मोफत सामग्री प्रकाशन, सशुल्क जाहिरात, Cold Outreach) समावेश असेल आणि लक्ष्यित मोफत सामग्री निर्मितीद्वारे तुमची तज्ञता कशी प्रस्थापित करता येते, संभाव्य ग्राहकांचा विश्वास कसा संपादन करता येतो आणि अभ्यागतांना निष्ठावान ग्राहकांमध्ये कसे रूपांतरित करता येते, तसेच तुमची ऑनलाइन दृश्यमानता आणि विश्वासार्हता कशी सुधारता येते यावर भर दिला जाईल.`,
    },
  },
  [LanguageCode.basque]: {
    comparison: {
      presentation: 'Bilatu produktu bat bere lehiakide nagusiak ikusteko.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Bilatu kategoria bat produktu onenak aurkitzeko.`,
      try: `Emakumeentzako mendiko motxila bat nahi dut`,
    },
    article: {
      presentation: `Bilatu artikulu bat sortzeko.`,
      try: `Artikulu bat nahi dut bezeroak erakartzeko doako edukiaren garrantziari buruz, bezero-eskuratzeko lau estrategia nagusiak (Warm Outreach, Doako Edukiaren Argitalpena, Ordainpeko Publizitatea, Cold Outreach) jorratuz eta doako eduki zuzenduaren sorkuntzak zure aditutasuna ezartzeko, prospektuekin konfiantza sortzeko eta bisitariak bezero leial bihurtzeko nola lagun dezakeen azpimarratuz, lineako ikusgarritasuna eta sinesgarritasuna hobetuz.`,
    },
  },
  [LanguageCode.romansh]: {
    comparison: {
      presentation: 'Tschertgai in product per vesair ses megliers concurrents.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Tschertgai ina categoria per chattar ils megliers products.`,
      try: `Jau vuless in rucsac da viandar per dunnas`,
    },
    article: {
      presentation: `Tschertgai in artitgel per al crear.`,
      try: `Jau vuless in artitgel davart l'impurtanza da cuntegn gratuit per attrar clients, cun las quatter strategias principalas per acquisiziun da clients (Warm Outreach, publicaziun da cuntegn gratuit, reclama pajada, Cold Outreach) e cun focussar sin co cuntegn gratuit mirai po stabilir Vossa expertisa, construir confidenza tar prospects e transfurmar visitaders en clients fidaivels, entant che megliereschar Vossa visibilitad ed credibilitad online.`,
    },
  },
  [LanguageCode.catalan]: {
    comparison: {
      presentation: 'Cerqueu un producte per veure els seus millors competidors.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Cerqueu una categoria per trobar els millors productes.`,
      try: `Vull una motxilla de senderisme per a dones`,
    },
    article: {
      presentation: `Cerqueu un article per crear-lo.`,
      try: `Vull un article sobre la importància del contingut gratuït per atraure clients, abordant les quatre estratègies principals d'adquisició de clients (Warm Outreach, publicació de contingut gratuït, publicitat de pagament, Cold Outreach) i centrant-se en com la creació de contingut gratuït dirigit pot establir la vostra experiència, generar confiança amb els potencials clients i convertir els visitants en clients fidels, alhora que millora la vostra visibilitat i credibilitat en línia.`,
    },
  },
  [LanguageCode.hindi]: {
    comparison: {
      presentation: 'किसी उत्पाद को खोजें और उसके शीर्ष प्रतिस्पर्धियों को देखें।',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `सर्वश्रेष्ठ उत्पाद खोजने के लिए कोई श्रेणी खोजें।`,
      try: `मुझे महिलाओं के लिए एक हाइकिंग बैकपैक चाहिए`,
    },
    article: {
      presentation: `कोई लेख बनाने के लिए उसे खोजें।`,
      try: `मुझे ग्राहकों को आकर्षित करने के लिए मुफ़्त सामग्री के महत्व पर एक लेख चाहिए, जिसमें ग्राहक अधिग्रहण की चार प्रमुख रणनीतियों (Warm Outreach, मुफ़्त सामग्री प्रकाशन, सशुल्क विज्ञापन, Cold Outreach) पर चर्चा हो और इस पर ध्यान केंद्रित हो कि कैसे लक्षित मुफ़्त सामग्री निर्माण आपकी विशेषज्ञता स्थापित कर सकता है, संभावित ग्राहकों में विश्वास बना सकता है, और आगंतुकों को वफ़ादार ग्राहकों में बदल सकता है, साथ ही आपकी ऑनलाइन दृश्यता और विश्वसनीयता में सुधार कर सकता है।`,
    },
  },
  [LanguageCode.irish]: {
    comparison: {
      presentation: 'Cuardaigh táirge chun a phríomhiomaitheoirí a fheiceáil.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Cuardaigh catagóir chun na táirgí is fearr a aimsiú.`,
      try: `Ba mhaith liom mála droma siúlóide do mhná`,
    },
    article: {
      presentation: `Cuardaigh alt chun é a chruthú.`,
      try: `Ba mhaith liom alt faoi thábhacht an ábhair shaor in aisce chun custaiméirí a mhealladh, ag plé na gceithre phríomhstraitéis chun custaiméirí a fháil (Teagmháil The, Foilsiú Ábhair Shaor in Aisce, Fógraíocht Íoctha, Teagmháil Fhuar) agus ag díriú ar an gcaoi a bhféadfadh cruthú ábhair shaor in aisce spriocdhírithe do shaineolas a bhunú, muinín a thógáil le hionchais, agus cuairteoirí a thiontú ina gcustaiméirí dílis, agus d'infheictheacht agus do chreidiúnacht ar líne á bhfeabhsú ag an am céanna.`,
    },
  },
  [LanguageCode.tamil]: {
    comparison: {
      presentation: 'ஒரு பொருளைத் தேடி அதன் சிறந்த போட்டியாளர்களைக் காணுங்கள்.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `சிறந்த பொருட்களைக் கண்டறிய ஒரு வகையைத் தேடுங்கள்.`,
      try: `பெண்களுக்கான மலையேற்றப் பின்சுமை`,
    },
    article: {
      presentation: `ஒரு கட்டுரையை உருவாக்க அதைத் தேடுங்கள்.`,
      try: `வாடிக்கையாளர்களை ஈர்ப்பதற்கான இலவச உள்ளடக்கத்தின் முக்கியத்துவம் குறித்த கட்டுரை வேண்டும், நான்கு முக்கிய வாடிக்கையாளர் ஈர்ப்பு உத்திகளை (நெருக்கமான தொடர்பு, இலவச உள்ளடக்க வெளியீடு, பணம் செலுத்தி விளம்பரம், குளிர் தொடர்பு) விவரிக்கவும், இலக்கு வைத்த இலவச உள்ளடக்கம் உங்கள் நிபுணத்துவத்தை நிலைநாட்டி, வாய்ப்புள்ளவர்களிடம் நம்பிக்கையை வளர்த்து, பார்வையாளர்களை விசுவாசமான வாடிக்கையாளர்களாக மாற்றுவது எப்படி என்பதில் கவனம் செலுத்தவும்.`,
    },
  },
  [LanguageCode.maori]: {
    comparison: {
      presentation: 'Rapua he hua kia kitea ngā whakataetae tino pai.',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `Rapua he wāhanga kia kitea ngā hua tino pai.`,
      try: `He pēke hikoi mō ngā wāhine`,
    },
    article: {
      presentation: `Rapua he tuhinga kia hangaia.`,
      try: `He tuhinga e pā ana ki te hiranga o ngā ihirangi koreutu hei kukume kiritaki, e whai ana i ngā rautaki matua e whā o te kukume kiritaki (Warm Outreach, Free Content Publishing, Paid Advertising, Cold Outreach) me te arotahi ki te āhua o te hanga ihirangi koreutu ka whakatū i tō mātauranga, ka whakarite whakawhirinaki ki ngā kiritaki tūmanako, ka huri manuhiri hei kiritaki pūmau, me te whakapai ake i tō kitea me tō whakapaetanga ipurangi.`,
    },
  },
  [LanguageCode.japanese]: {
    comparison: {
      presentation: '製品を検索して、主要な競合製品を確認しましょう。',
      try: `iPhone 18 Pro`,
    },
    category: {
      presentation: `カテゴリーを検索して、おすすめの製品を見つけましょう。`,
      try: `女性向けのハイキング用バックパックが欲しい`,
    },
    article: {
      presentation: `記事を検索して作成しましょう。`,
      try: `顧客を惹きつけるための無料コンテンツの重要性に関する記事を作成したいです。顧客獲得の4つの主要戦略（ウォームアウトリーチ、無料コンテンツの公開、有料広告、コールドアウトリーチ）を取り上げ、ターゲットを絞った無料コンテンツの作成がいかに専門性を確立し、見込み客との信頼関係を構築し、訪問者を忠実な顧客へと転換できるかに焦点を当て、同時にオンラインでの認知度と信頼性を向上させる方法について解説してください。`,
    },
  },
};

export const demoContent: Record<LanguageType, DemoType> =
  proxyLanguageRecord<DemoType>(demoContentCore);

/**
 * Examples read as a continuation of the search bar's own prompt, so they
 * start on a lowercase letter — the rest of the string keeps its casing.
 * The first letter is left alone when the second character is also uppercase,
 * so acronyms and brands ("USB-C hub", "iPhone") are never mangled; scripts
 * without letter case (ja, hi, ta…) pass through untouched.
 */
function startLowercase(text: string): string {
  const first = text.charAt(0);
  // Already lowercase, or a caseless script.
  if (!first || first !== first.toUpperCase()) return text;
  const second = text.charAt(1);
  const secondIsUppercase = second && second === second.toUpperCase() && second !== second.toLowerCase();
  if (secondIsUppercase) return text;
  return first.toLowerCase() + text.slice(1);
}

/**
 * Example queries for a locale — single source of truth for the rotating
 * placeholder and the suggestion chips. The proxy record already falls whole-
 * language back to English; partially translated languages without a curated
 * `examples` list get their own two localized examples instead.
 */
export function getSearchExamples(locale: LocaleDetails): string[] {
  const content = demoContent[locale.languageCode];
  const examples =
    content.examples && content.examples.length > 0
      ? content.examples
      : [content.category.try, content.comparison.try];
  return examples.map(startLowercase);
}
